import { z } from "zod";
import { UserRole } from "./enums";
import { TagUidSchema } from "./timeclock";

/**
 * User + auth-request wire schemas.
 *
 * `User` is the session-visible user record (no password/pin hashes, no
 * pin-attempt counters — those never leave the server).
 */

export const User = z.object({
  id: z.string().uuid(),
  username: z.string().nullable(),
  name: z.string(),
  phone: z.string().nullable(),
  role: UserRole,
  isActive: z.boolean(),
  /**
   * Unit #19: per-dispatcher create toggle. Always present on the
   * wire (so the UI doesn't have to defensively check), but only
   * meaningful for DISPATCHER role. DRIVER and OFFICE rows also
   * carry the value (always `true` for them — the column is NOT
   * NULL DEFAULT true) so the wire shape is uniform across roles.
   */
  canCreateJobs: z.boolean(),
  /**
   * Unit #20: three more per-dispatcher toggles for in-form reference
   * creation. Same role shape (always present, only meaningful for
   * DISPATCHER). UI source — the server still re-reads the DB column
   * per request on the relevant POST branches (see
   * `apps/api/src/routes/reference.ts`).
   */
  canCreateCustomers: z.boolean(),
  canCreateProperties: z.boolean(),
  canCreateLandfills: z.boolean(),
  /**
   * SPEC-75 — display-only dispatcher account facts. OPTIONAL because only
   * GET /dispatchers (and the two reset routes' consumers) carry them;
   * drivers' shapes and /auth/me omit them (Zod strip mode tolerates the
   * extra keys server-side, and absent must NOT be read as false by UI that
   * doesn't know the field). No route TRUSTS these for enforcement — the
   * lockout gate re-reads users.password_locked server-side, and binding
   * gates re-read device_sessions.
   */
  deviceBound: z.boolean().optional(),
  deviceBoundAt: z.string().datetime().nullable().optional(),
  passwordLocked: z.boolean().optional(),
});
export type User = z.infer<typeof User>;

/**
 * SPEC-75 §2.3.c — body of POST /dispatchers/:id/reset-password (office).
 * Lives in shared (THE API contract) because apps/api has no direct zod
 * dependency. Password bounds mirror NewDispatcher's create contract (min 6)
 * rather than the spec draft's min-8: §2.3.c cites "NewDispatcher's password
 * constraints — min 8" but NewDispatcher itself enforces min 6, and matching
 * the create contract avoids two different strengths for one factor.
 */
export const ResetDispatcherPassword = z.object({
  password: z.string().min(6).max(128),
});
export type ResetDispatcherPassword = z.infer<typeof ResetDispatcherPassword>;

// POST /auth/login (dispatcher/office)
export const LoginRequest = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
  // SPEC-68 §3.5 — explicit "I am skipping my passkey". Only `true` acts;
  // `false` is identical to absent. DISPATCHER only: an OFFICE request
  // carrying `true` is rejected 403 fallback_not_permitted (§4.5c).
  fallback: z.boolean().optional(),
  // SPEC-70 — presence factor. DISPATCHER-only at the server (OFFICE ignores
  // the field; sending it as OFFICE is not an error).
  tagUid: TagUidSchema.optional(), // normalizes colon/lowercase forms (audit #3)
});
export type LoginRequest = z.infer<typeof LoginRequest>;

// POST /auth/pin-login (driver)
export const PinLoginRequest = z.object({
  userId: z.string().uuid(), // which driver name was tapped
  pin: z.string().regex(/^\d{4}$/),
  tagUid: TagUidSchema.optional(), // SPEC-70 — presence factor, DRIVER
});
export type PinLoginRequest = z.infer<typeof PinLoginRequest>;

export const PinLoginResponse = z.object({
  user: User,
  accessToken: z.string(),
  driverId: z.string().uuid(),
  fallbackSession: z.boolean(), // SPEC-68 §3.5 — observable session class
});
export type PinLoginResponse = z.infer<typeof PinLoginResponse>;

// POST /dispatchers (office)
export const NewDispatcher = z.object({
  username: z.string().min(1).max(100),
  password: z.string().min(6),
  name: z.string().min(1).max(100),
  phone: z.string().nullable().optional(),
});
export type NewDispatcher = z.infer<typeof NewDispatcher>;

/**
 * PATCH /dispatchers/:id (office) — Unit #19 / #20 (flags) + DISPATCHER-EDIT
 * (name / username / password). Every key is optional — the office flips one
 * or more at a time. `.strict()` rejects unknown keys at the wire boundary.
 * The route only writes the keys that are PRESENT in the body (partial
 * update); the audit row records `{changedKey: {before, after}}` for each
 * change. Password never carries a value into the audit (see the route).
 *
 * String bounds mirror `NewDispatcher` (the create contract): name 1-100,
 * username 1-100, password min 6.
 */
export const PatchDispatcher = z
  .object({
    canCreateJobs: z.boolean().optional(),
    canCreateCustomers: z.boolean().optional(),
    canCreateProperties: z.boolean().optional(),
    canCreateLandfills: z.boolean().optional(),
    isActive: z.boolean().optional(),
    name: z.string().min(1).max(100).optional(),
    username: z.string().min(1).max(100).optional(),
    password: z.string().min(6).optional(),
  })
  .strict()
  .refine(
    (d) =>
      d.canCreateJobs !== undefined ||
      d.canCreateCustomers !== undefined ||
      d.canCreateProperties !== undefined ||
      d.canCreateLandfills !== undefined ||
      d.isActive !== undefined ||
      d.name !== undefined ||
      d.username !== undefined ||
      d.password !== undefined,
    { message: "at least one field is required" },
  );
export type PatchDispatcher = z.infer<typeof PatchDispatcher>;
